import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, BookOpen, Calendar, Gift, IdCard, Lightbulb, FileText, Bell, MessageSquareText, ScanLine, Sparkles, Trophy, FolderOpen, Headphones as HeadphonesIcon, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatCourseDate } from "@/lib/dates";

const quickActions = (area) => [
  { to: "/carteira", icon: IdCard, label: "Carteira Digital", color: "bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))]" },
  { to: "/certificados", icon: Award, label: "Certificados", color: "bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))]" },
  { to: "/conquistas", icon: Trophy, label: "Conquistas", color: "bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))]" },
  { to: "/calendario", icon: Calendar, label: "Calendário de Cursos", color: "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]" },
  { to: "/aulas-gratuitas", icon: BookOpen, label: "Aulas Gratuitas", color: "bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))]" },
  { to: "/materiais", icon: FolderOpen, label: "Materiais", color: "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]" },
  { to: "/banco-ideias", icon: Lightbulb, label: "Banco de PLs", color: "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]" },
  { to: "/consultorias", icon: HeadphonesIcon, label: "Consultorias", color: "bg-[hsl(var(--accent)/0.1)] text-[hsl(var(--accent))]" },
  { to: "/beneficios", icon: Gift, label: "Convênios", color: "bg-[hsl(var(--gold)/0.18)] text-[hsl(var(--gold-foreground))]" },
  { to: "/sugestoes", icon: MessageSquareText, label: "Sugerir cursos", color: "bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]" },
];

function QuickGrid({ area }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="home-quick-actions">
      {quickActions(area).map((qa) => {
        const Icon = qa.icon;
        return (
          <Link key={qa.to + qa.label} to={qa.to} className="flex flex-col items-center gap-1.5 group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${qa.color} group-hover:scale-[1.03] transition-transform`}>
              <Icon className="w-5 h-5" />
            </div>
            <span className="text-[10.5px] text-center text-[hsl(var(--primary))] font-medium leading-tight">{qa.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

function CourseCard({ c }) {
  const startLabel = formatCourseDate(c?.start_date);
  return (
    <Link to={`/calendario/${c.id}`} className="block" data-testid={`home-course-${c.id}`}>
      <Card className="rounded-xl hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="capitalize">{c.area}</Badge>
            <Badge variant="outline">{c.workload_hours}h</Badge>
          </div>
          <h4 className="font-display font-semibold text-base text-[hsl(var(--primary))] line-clamp-2">{c.title}</h4>
          <p className="text-xs text-muted-foreground mt-1">{c.cidade}/{c.estado}</p>
          {startLabel && (
            <p className="text-xs text-[hsl(var(--accent))] font-medium mt-1">{startLabel}</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function FreeCourseCard({ c }) {
  return (
    <Link to={`/aulas-gratuitas/${c.id}`} className="block" data-testid={`home-free-${c.id}`}>
      <Card className="rounded-xl overflow-hidden hover:shadow-md transition-shadow">
        {c.thumbnail_url ? (
          <div className="aspect-video bg-[hsl(var(--secondary))] overflow-hidden">
            <img src={c.thumbnail_url} alt={c.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-video bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] flex items-center justify-center text-white">
            <BookOpen className="w-10 h-10 opacity-80" />
          </div>
        )}
        <CardContent className="p-3">
          <Badge variant="secondary" className="text-[10px] mb-1 capitalize">{c.area}</Badge>
          <h4 className="font-display font-medium text-sm line-clamp-2 text-[hsl(var(--primary))]">{c.title}</h4>
          <p className="text-[11px] text-muted-foreground mt-1">{c.duration_minutes} min • {c.professor || "CEAP"}</p>
        </CardContent>
      </Card>
    </Link>
  );
}

function AreaPanel({ area, user }) {
  const [courses, setCourses] = useState([]);
  const [free, setFree] = useState([]);
  const [newCerts, setNewCerts] = useState([]);
  useEffect(() => {
    api.get("/courses", { params: { area } }).then(r => setCourses(r.data.slice(0, 4))).catch(() => {});
    api.get("/free-courses", { params: { area } }).then(r => setFree(r.data.slice(0, 4))).catch(() => {});
    api.get("/home/highlights").then(r => setNewCerts(r.data?.certificates_new || [])).catch(() => {});
  }, [area]);
  return (
    <div className="space-y-6">
      <Card className="rounded-2xl border-[hsl(var(--accent)/0.25)] bg-[hsl(var(--accent)/0.04)]">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent))] text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Bem-vindo(a) novamente</p>
            <p className="font-display font-semibold text-sm text-[hsl(var(--primary))] truncate">{user.full_name}</p>
            <p className="text-[11px] text-muted-foreground">{user.ceap_id} • {user.level}</p>
          </div>
          <Link to="/carteira" className="ml-auto text-xs font-medium text-[hsl(var(--accent))] hover:underline">Ver carteira</Link>
        </CardContent>
      </Card>

      {newCerts.length > 0 && (
        <Card
          className="rounded-2xl border-[hsl(var(--gold)/0.5)] bg-gradient-to-br from-[hsl(var(--gold)/0.15)] via-[hsl(var(--gold)/0.05)] to-transparent overflow-hidden relative"
          data-testid="home-new-certificates"
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--gold)/0.25)] text-[hsl(var(--gold-foreground))] flex items-center justify-center flex-shrink-0">
                <PartyPopper className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm text-[hsl(var(--primary))]">
                  {newCerts.length === 1
                    ? "Parabéns! Você tem um novo certificado"
                    : `Parabéns! Você tem ${newCerts.length} novos certificados`}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {newCerts.length === 1
                    ? "Está disponível para emissão. Clique abaixo para baixar."
                    : "Estão disponíveis para emissão. Clique abaixo para baixá-los."}
                </p>
                <div className="space-y-1.5 mt-3">
                  {newCerts.slice(0, 2).map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/70 border border-[hsl(var(--gold)/0.3)] text-xs"
                      data-testid={`home-new-cert-${c.id}`}
                    >
                      <Award className="w-3.5 h-3.5 text-[hsl(var(--gold-foreground))] flex-shrink-0" />
                      <span className="font-medium text-[hsl(var(--primary))] truncate">{c.course_name}</span>
                    </div>
                  ))}
                  {newCerts.length > 2 && (
                    <p className="text-[11px] text-muted-foreground px-1">+ {newCerts.length - 2} outros certificados</p>
                  )}
                </div>
                <Button
                  asChild
                  size="sm"
                  className="mt-3 w-full bg-[hsl(var(--gold-foreground))] hover:bg-[hsl(var(--primary))] text-white"
                  data-testid="home-new-cert-cta"
                >
                  <Link to="/certificados">
                    <Award className="w-4 h-4 mr-1.5" />
                    {newCerts.length === 1 ? "Emitir meu certificado" : "Emitir meus certificados"}
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <QuickGrid area={area} />

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-base">Próximos cursos</h2>
          <Link to="/calendario" className="text-xs font-medium text-[hsl(var(--accent))]">Ver todos</Link>
        </div>
        {courses.length === 0 ? (
          <Card className="rounded-xl"><CardContent className="p-4 text-sm text-muted-foreground">Em breve novos cursos para sua área.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="home-upcoming-courses">
            {courses.map(c => <CourseCard key={c.id} c={c} />)}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-base">Aulas gratuitas</h2>
          <Link to="/aulas-gratuitas" className="text-xs font-medium text-[hsl(var(--accent))]">Ver todas</Link>
        </div>
        {free.length === 0 ? (
          <Card className="rounded-xl"><CardContent className="p-4 text-sm text-muted-foreground">Nenhum curso gratuito por enquanto.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {free.map(c => <FreeCourseCard key={c.id} c={c} />)}
          </div>
        )}
      </section>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.profile === "ambos") {
    return (
      <div data-testid="home-page">
        <Tabs defaultValue="legislativo" className="w-full">
          <TabsList className="grid grid-cols-2 w-full mb-4" data-testid="home-profile-tabs">
            <TabsTrigger value="legislativo" data-testid="tab-legislativo">Legislativo</TabsTrigger>
            <TabsTrigger value="licitacoes" data-testid="tab-licitacoes">Licitações</TabsTrigger>
          </TabsList>
          <TabsContent value="legislativo"><AreaPanel area="legislativo" user={user} /></TabsContent>
          <TabsContent value="licitacoes"><AreaPanel area="licitacoes" user={user} /></TabsContent>
        </Tabs>
      </div>
    );
  }
  return <div data-testid="home-page"><AreaPanel area={user.profile} user={user} /></div>;
}
